import * as _ from 'lodash';
import * as React from 'react';
import { connect } from 'react-redux';
import { Container, Form, FormGroup, Input, Label, Table } from 'reactstrap';
import { bindActionCreators, Dispatch } from 'redux';

import * as BeneficiarySearchAction from '../../actions/BeneficiarySearch';

import * as BillAidsApi from '../../api/billAids';
import * as PartnersApi from '../../api/partners';

import Localization from '../../config/localization';
import { SECTOR_ID_GAS } from '../../constants/sectorId';

import Notifications from '../../lib/notifications';

import BackButton from '../../components/BackButton';
import Client from '../../components/Client';
import Loader from '../../components/Loader';

import { Add0, GetMonthFromSeason, GetYearFromSeason, ToFloat, ToNumber, ToString } from '../../lib/utils';
import { CNPValidation } from '../../lib/validator';

import './BilledAidsHistory.scss';
import { getQuantumAmount } from '../../lib/money';

type IProps = BilledAidsHistory.IProps & ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

class BilledAidsHistory extends React.Component<IProps, BilledAidsHistory.IState> {
  state: BilledAidsHistory.IState = {
    loading: false,
    loadingMessage: '',

    billedAidsHistory: undefined,
    partner: undefined,
    search: (this.props.BeneficiarySearch && this.props.BeneficiarySearch.search) || ''
  };

  componentDidMount() {
    if (this.state.search) {
      this._getData({ preventDefault: () => undefined } as React.FormEvent<HTMLFormElement>);
    }
  }

  renderTableBody = () => {
    const billedAidsHistory = this.state.billedAidsHistory;

    if (!billedAidsHistory || !billedAidsHistory.length) {
      return null;
    }

    const groupBy = _.groupBy(billedAidsHistory, 'month');

    return Object.keys(groupBy).map(month =>
      groupBy[month]
        .map((billAidHistory, index) => (
          <tr key={index} className="eon-middle-grey">
            <td>{getQuantumAmount(billAidHistory)}</td>
            <td>{this._getMonthString(billAidHistory.month)}</td>
            <td>{ToFloat(billAidHistory.dueAmount)}</td>
            <td>{this._getMonthString(billAidHistory.monthDecont)}</td>
            <td>{ToString(Localization.billedAidsHistoryStatus[billAidHistory.status])}</td>
          </tr>
        ))
        .concat(
          <tr key={month} className="eon-black">
            <td />
            <td>{this._getMonthString(month as keyof Month.IGetMonths)}</td>
            <td>{this._countBilledAidsDueAmountTotal(groupBy[month])}</td>
            <td />
            <td />
          </tr>
        )
    );
  };

  renderTable = () => {
    return (
      <Table className="my-5 text-left bg-eon-white" hover>
        <thead>
        <tr>
          <th className="align-top" scope="col">
            <p className="eon-title eon-red">{Localization.billedAidsHistoryTabelBudgetAmount}</p>
          </th>
          <th className="align-top" scope="col">
            <p className="eon-title eon-red">{Localization.billedAidsHistoryTabelMonth}</p>
          </th>
          <th className="align-top" scope="col">
            <p className="eon-title eon-red">{Localization.billedAidsHistoryTabelEffectiveBudgetAmount}</p>
          </th>
          <th className="align-top" scope="col">
            <p className="eon-title eon-red">{Localization.billedAidsHistoryTabelDecontMonth}</p>
          </th>
          <th className="align-top" scope="col">
            <p className="eon-title eon-red">{Localization.billedAidsHistoryTabelDecontType}</p>
          </th>
        </tr>
        </thead>
        <tbody>{this.renderTableBody()}</tbody>
      </Table>
    );
  };

  render() {
    return (
      <Container className="billed-aids-history px-5 py-4 my-4 position-relative">
        <BackButton />

        <Loader loading={this.state.loading} loadingMessage={this.state.loadingMessage} />

        {this.state.partner && (
          <Client
            hideTitle
            partner={this.state.partner}
            isNaturalGas={this._isNaturalGas()}
            showUtilityType={true}
            extraClassName="client-card position-absolute"
          />
        )}

        <h4 className="mt-5 eon-title eon-red force-wrap">{Localization.billedAidsHistoryTitle}</h4>

        <Form className="mt-5 w-30" onSubmit={this._getData}>
          <FormGroup>
            <Label for="search">{Localization.billedAidsHistorySearchLabel}</Label>
            <Input
              type="text"
              name="search"
              id="search"
              className="eon-input-text eon-input-text-no-label"
              onChange={this._setSearch}
              value={this.state.search}
              placeholder={Localization.billedAidsHistorySearchPlaceholder}
            />
          </FormGroup>
          <button className="eon-button bg-eon-red eon-white mt-4" disabled={!this.state.search}>
            <span className="px-4">{Localization.billedAidsHistorySearchButton}</span>
          </button>
        </Form>

        {this.renderTable()}
      </Container>
    );
  }

  _getMonthString = (season: keyof Month.IGetMonths) => {
    return `${Localization.Months[Add0(GetMonthFromSeason(season))]} ${GetYearFromSeason(season)}`;
  };

  _countBilledAidsDueAmountTotal = (billedAidsHistory?: BillAids.IBillAidsHistoryResponse[]) => {
    if (!billedAidsHistory) {
      return 0;
    } else if (billedAidsHistory.length < 2) {
      return ToFloat(billedAidsHistory[0].dueAmount);
    }

    return ToFloat(
      billedAidsHistory
        .map(billedAidHistory => ToFloat(billedAidHistory && billedAidHistory.dueAmount))
        .reduce((a, b) => ToFloat(a) + ToFloat(b))
    );
  };

  _setSearch = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const element = event.currentTarget || event.target || {};

    await this.setState({ search: ToString(ToNumber(element.value)).trim() });

    await this.props.ClearSearch();

    return this.props.SetSearch(this.state.search);
  };

  _isNaturalGas = () => {
    return !!(
      this.state.partner &&
      this.state.partner.consumptionPoints &&
      this.state.partner.consumptionPoints.length &&
      this.state.partner.consumptionPoints[0] &&
      this.state.partner.consumptionPoints[0].sectorId === SECTOR_ID_GAS
    );
  };

  _getData = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!CNPValidation(this.state.search)) {
      return Notifications.error(Localization.billedAidsHistoryInvalidCNP);
    }

    return Promise.all([this._getPartner(), this._getResults()]);
  };

  _getResults = async () => {
    try {
      await this.setState({ loading: true });

      const response = await BillAidsApi.HistorySearch(this.state.search);

      return this.setState({ loading: false, billedAidsHistory: response.data });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _getPartner = async () => {
    try {
      await this.setState({ loading: true });

      const response = await PartnersApi.GetPartner({ search: this.state.search });

      return this.setState({ loading: false, partner: (response.data && response.data[0]) || {} });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };
}

const mapStateToProps = (state: General.Store) => ({ BeneficiarySearch: state.BeneficiarySearch });

const mapDispatchToProps = (dispatch: Dispatch) => ({ ...bindActionCreators({ ...BeneficiarySearchAction }, dispatch) });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BilledAidsHistory);
